import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Lock, Brain, BarChart3, TrendingUp, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';
import BrandName from '../components/BrandName';

const iconMap = {
  Lock: Lock,
  Brain: Brain,
  BarChart3: BarChart3,
  TrendingUp: TrendingUp,
  Shield: Shield,
  Zap: Zap,
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Kinetic Animation */}
      <motion.section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ opacity }}
      >
        {/* Animated Background */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{
            background: 'linear-gradient(180deg, #FFFFFF 0%, #A9C9D9 50%, #7CB8D6 100%)',
            y,
          }}
        >
          {/* Floating Elements */}
          <motion.div
            className="absolute top-20 left-20 w-64 h-64 rounded-full"
            style={{ background: 'rgba(244, 215, 64, 0.2)', filter: 'blur(60px)' }}
            animate={{
              y: [0, 30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full"
            style={{ background: 'rgba(124, 184, 214, 0.3)', filter: 'blur(80px)' }}
            animate={{
              y: [0, -40, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.div>

        {/* Hero Content with Line-by-Line Reveal */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center pt-24 md:pt-20 pb-32">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <MaskedTextReveal text="Blockchain-Backed AI" delay={0.2} />
            <MaskedTextReveal text="for a Net-Zero Future" delay={0.5} />
            
            <motion.p
              className="text-xl md:text-2xl text-gray-700 mb-12 max-w-3xl mx-auto mt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Transform your sustainability journey with transparent, AI-powered ESG intelligence
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <Link to="/contact">
                <button className="btn-premium px-10 py-5 rounded-full text-lg font-semibold text-white relative overflow-hidden group"
                  style={{ background: '#7CB8D6' }}
                >
                  <span className="relative z-10">Request Demo</span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.6 }}
                  />
                </button>
              </Link>
              <Link to="/platform">
                <button className="px-10 py-5 rounded-full text-lg font-semibold border-2 hover:bg-gray-50 transition-all duration-300"
                  style={{ borderColor: '#7CB8D6', color: '#7CB8D6' }}
                >
                  Explore Platform
                </button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            {mockData.hero.stats.map((stat, index) => (
              <motion.div
                key={index}
                className="glass-morphism p-8 rounded-3xl"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-xl md:text-2xl font-bold mb-2 whitespace-nowrap" style={{ color: '#1A1A1A' }}>
                  {stat.value}
                </div>
                <div className="text-gray-600 text-xs md:text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2"
            style={{ borderColor: '#7CB8D6' }}
          >
            <motion.div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: '#7CB8D6' }}
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Manifesto Section - Numbered Chapters */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-5xl md:text-6xl font-bold text-center mb-24"
            style={{ color: '#1A1A1A' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Our Manifesto
          </motion.h2>
          
          <div className="space-y-32">
            {mockData.manifesto.map((item, index) => (
              <ManifestoChapter key={index} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
              Platform Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enterprise-grade tools for transparent, verifiable sustainability management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-12">
            {mockData.features.map((feature, index) => (
              <FeatureCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <section className="py-20 overflow-hidden" style={{ background: '#7CB8D6' }}>
        <MarqueeText />
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Ready to Transform Your ESG Strategy?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Partner with <BrandName /> to build a transparent, AI-powered sustainability strategy from day one
            </p>
            <Link to="/contact">
              <motion.button
                className="btn-premium px-12 py-6 rounded-full text-xl font-semibold text-white"
                style={{ background: '#F4D740', color: '#1A1A1A' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule a Demo
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Masked Text Reveal Component
const MaskedTextReveal = ({ text, delay }) => {
  return (
    <motion.h1
      className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold mb-4"
      style={{ color: '#1A1A1A' }}
      initial={{ clipPath: 'inset(0 100% 0 0)' }}
      animate={{ clipPath: 'inset(0 0 0 0)' }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.h1>
  );
};

// Manifesto Chapter Component
const ManifestoChapter = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className="flex flex-col md:flex-row gap-12 items-start"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.2 }}
    >
      <div className="text-8xl md:text-9xl font-bold opacity-20" style={{ color: '#7CB8D6' }}>
        {item.number}
      </div>
      <div className="flex-1">
        <h3 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
          {item.title}
        </h3>
        <p className="text-xl text-gray-600 leading-relaxed">
          {item.description}
        </p>
      </div>
    </motion.div>
  );
};

// Feature Card Component
const FeatureCard = ({ feature, index }) => {
  const Icon = iconMap[feature.icon] || Shield;
  
  return (
    <motion.div
      className="card-premium p-10 rounded-3xl bg-white border-2 border-gray-100"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="text-5xl font-bold opacity-30" style={{ color: '#7CB8D6' }}>
          {feature.number}
        </div>
        <div className="p-4 rounded-2xl" style={{ background: 'rgba(124, 184, 214, 0.1)' }}>
          <Icon size={32} style={{ color: '#7CB8D6' }} />
        </div>
      </div>
      <h3 className="text-2xl font-bold mb-4" style={{ color: '#1A1A1A' }}>
        {feature.title}
      </h3>
      <p className="text-gray-600 mb-6 leading-relaxed">
        {feature.description}
      </p>
      <ul className="space-y-3">
        {feature.benefits.map((benefit, i) => (
          <li key={i} className="flex items-center text-gray-700">
            <div className="w-1.5 h-1.5 rounded-full mr-3" style={{ background: '#F4D740' }} />
            {benefit}
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

// Marquee Component
const MarqueeText = () => {
  const text = 'Transparent ESG · Blockchain Verified · AI-Powered Insights · ';
  const repeatedText = text.repeat(10);

  return (
    <div className="relative flex overflow-hidden">
      <motion.div
        className="flex whitespace-nowrap"
        animate={{ x: ['-50%', '0%'] }}
        transition={{
          duration: 60,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        <span className="text-4xl md:text-6xl font-bold text-white">
          {repeatedText}
        </span>
        <span className="text-4xl md:text-6xl font-bold text-white">
          {repeatedText}
        </span>
      </motion.div>
    </div>
  );
};

export default Home;